import { router } from "@inertiajs/react"
import { Archive, FilePlus2, Plus, RotateCcw, Scale, Rocket, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import type { SkillVersionRow } from "@/types/skill"
import type { RehearsalSummary } from "@/types/test_lab"

type Props = {
  skillId: number
  selectedVersion: number
  versions: SkillVersionRow[]
  isGlobal: boolean
  onSwitchVersion: (versionId: number) => void
  // Promote-gate state. When undefined (e.g. on pages outside the Test Lab
  // that don't compute the gate), the button stays enabled and the server
  // does the safety check. When supplied, the UI greys out the button +
  // captions it so the operator knows why.
  canPromote?: boolean
  blockerCount?: number
  // Informational rehearsal state. Rehearsals no longer block promotion;
  // callers can still pass this for adjacent UI surfaces.
  rehearsal?: RehearsalSummary | null
}

// Top-bar version selector: dropdown + Promote / Create / Compare actions.
// Compare is reserved for v1.1 (page exists in plan, not built in 2A).
export function VersionSelector({
  skillId,
  selectedVersion,
  versions,
  isGlobal,
  onSwitchVersion,
  canPromote,
  blockerCount,
}: Props) {
  const selected = versions.find((v) => v.version === selectedVersion) ?? versions[0]
  const isDraft = selected?.status === "draft"
  const isLive  = selected?.status === "live"
  const isArchived = selected?.status === "archived"
  const writable = !isGlobal && isDraft
  // Gate-blocked iff the caller passed a flag AND it's false. When the
  // flag is undefined (consumer didn't compute it) we don't pre-empt.
  const gateBlocked = canPromote === false
  const promoteDisabled = !writable || gateBlocked

  // Promotion is blocked by required draft checks, not rehearsal state.
  // Rehearsal stays informational elsewhere in the page.
  const blockerSummary = (() => {
    if (typeof blockerCount === "number" && blockerCount > 0) {
      return `${blockerCount} test${blockerCount === 1 ? "" : "s"} need${blockerCount === 1 ? "s" : ""} to pass against this draft`
    }
    return null
  })()
  const promoteTitle = gateBlocked && blockerSummary ? blockerSummary : undefined

  function createDraft() {
    if (!confirm(`Clone the latest version into a new draft?`)) return
    router.post(`/skills/${skillId}/draft_version`, {}, { preserveScroll: false })
  }

  function createBlankDraft() {
    if (!confirm(`Create a completely blank draft? Forge will build it from the skill description only.`)) return
    router.post(`/skills/${skillId}/draft_version`, { blank: true }, { preserveScroll: false })
  }

  function promote() {
    if (!selected) return
    if (!confirm(`Promote v${selected.version} to live? Production traffic switches over immediately.`)) return
    router.patch(`/skills/${skillId}/promote`, { skill_version_id: selected.id }, { preserveScroll: false })
  }

  // Bypass the promote gate. Server logs the bypass and decorates the
  // success flash with a warning prefix.
  function promoteAnyway() {
    if (!selected) return
    const bullets: string[] = []
    if (typeof blockerCount === "number" && blockerCount > 0) {
      bullets.push(`${blockerCount} test${blockerCount === 1 ? "" : "s"} on this skill haven't passed yet`)
    }
    const list = bullets.map((b) => ` \u2022 ${b}`).join("\n")
    const msg =
      `Promote anyway? This bypasses test gates. Bypassing:\n${list}\n\nType / continue to confirm.`
    if (!confirm(msg)) return
    router.patch(`/skills/${skillId}/promote`, { force: true, skill_version_id: selected.id }, { preserveScroll: false })
  }

  function changeSelectedState(nextState: "draft" | "archived") {
    if (!selected) return
    const verb = nextState === "archived" ? "Archive" : "Restore"
    const detail = nextState === "archived"
      ? "This hides it from draft workflows but keeps it in version history."
      : "This makes it editable again as a draft."
    if (!confirm(`${verb} v${selected.version}? ${detail}`)) return
    router.patch(
      `/skills/${skillId}/versions/${selected.id}/state`,
      { state: nextState },
      { preserveScroll: false },
    )
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-app-muted">Version</span>
        <select
          value={selectedVersion}
          onChange={(e) => onSwitchVersion(Number(e.target.value))}
          className="h-9 rounded-control border border-border bg-white/5 px-3 text-sm text-app-text outline-none transition focus:border-brand-red/50"
        >
          {versions.map((v) => (
            <option key={v.id} value={v.version}>
              {v.name ? `(v${v.version}) - ${v.name} - ${v.status}` : `(v${v.version}) - ${v.status}`}
            </option>
          ))}
        </select>
        {selected && (
          <Badge tone={isLive ? "gold" : isDraft ? "red" : "neutral"}>
            {selected.status}
          </Badge>
        )}
      </div>

      <span className="h-5 w-px bg-border" aria-hidden />

      <Button variant="ghost" size="sm" onClick={createDraft} disabled={isGlobal}>
        <Plus size={14} strokeWidth={2} className="mr-1" />
        New version
      </Button>

      <Button variant="ghost" size="sm" onClick={createBlankDraft} disabled={isGlobal}>
        <FilePlus2 size={14} strokeWidth={2} className="mr-1" />
        Blank draft
      </Button>

      <Button variant="ghost" size="sm" disabled title="Coming in v1.1">
        <Scale size={14} strokeWidth={2} className="mr-1" />
        Compare
      </Button>

      {isDraft && (
        <Button variant="ghost" size="sm" onClick={() => changeSelectedState("archived")} disabled={isGlobal}>
          <Archive size={14} strokeWidth={2} className="mr-1" />
          Archive
        </Button>
      )}

      {isArchived && (
        <Button variant="ghost" size="sm" onClick={() => changeSelectedState("draft")} disabled={isGlobal}>
          <RotateCcw size={14} strokeWidth={2} className="mr-1" />
          Restore draft
        </Button>
      )}

      {/* Gate state is exclusive — show one button. Green: standard
          Promote. Blocked + writable: red outline "Promote anyway" with
          confirm. Blocked + read-only: disabled standard button. */}
      <div className="flex flex-col items-end gap-1">
        {gateBlocked && writable ? (
          <button
            type="button"
            onClick={promoteAnyway}
            className="inline-flex items-center gap-1 rounded-control border border-status-error/40 bg-status-error/10 px-3 py-1.5 text-xs text-status-error transition hover:bg-status-error/20"
            title="Bypass promote gate — logs an audit warning"
          >
            <AlertTriangle size={12} strokeWidth={2.2} />
            Promote anyway
          </button>
        ) : (
          <Button variant="red" size="sm" onClick={promote} disabled={promoteDisabled} title={promoteTitle}>
            <Rocket size={14} strokeWidth={2} className="mr-1" />
            Promote draft
          </Button>
        )}
        {gateBlocked && blockerSummary && (
          <span className="inline-flex items-center gap-1 text-[10px] text-status-warning">
            <AlertTriangle size={10} strokeWidth={2.2} />
            {blockerSummary}
          </span>
        )}
      </div>
    </div>
  )
}
